import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Search, Settings2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MODULE_SETTINGS_PATHS as MS } from "@/routes/company/module-settings.paths";
import { cn } from "@/utils/cn";
import {
  useGetBranchScopeOptionsQuery,
  type IPayrollBranch,
} from "@/services/(company)/payrollRunsApi";
import type { ScopeItem, ScopeKind, Subscope } from "./scope-data";
import { useTranslation } from "react-i18next";

export type { ScopeItem, ScopeKind } from "./scope-data";

const KIND_AVATAR_STYLES: Record<ScopeKind, string> = {
  branch: "bg-orange-100 text-orange-600",
  department: "bg-primary/10 text-primary",
  group: "bg-teal-100 text-teal-700",
};

const DEPT_PALETTE = [
  "bg-primary/10 text-primary",
  "bg-pink-100 text-pink-600",
  "bg-emerald-100 text-emerald-700",
  "bg-sky-100 text-sky-700",
  "bg-amber-100 text-amber-700",
  "bg-violet-100 text-violet-700",
];

const getInitials = (name: string) =>
  name
    .replace(/[^a-zA-Z\s]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const Monogram = ({
  kind,
  name,
  paletteIndex,
}: {
  kind: ScopeKind;
  name: string;
  paletteIndex?: number;
}) => (
  <div
    className={cn(
      "size-9 shrink-0 rounded-xs flex items-center justify-center text-xs font-bold",
      kind === "department" && paletteIndex !== undefined
        ? DEPT_PALETTE[paletteIndex % DEPT_PALETTE.length]
        : KIND_AVATAR_STYLES[kind],
    )}
  >
    {getInitials(name)}
  </div>
);

/**
 * The wizard consumes payroll groups but cannot create or edit one — they are
 * built per branch under Payroll Settings. Without this link the user has to
 * already know that, so an empty Custom Groups list reads as "this branch has
 * no groups" rather than "no groups have been set up yet".
 */
const ManageGroupsLink = ({ branchId }: { branchId: number }) => {
  const { t } = useTranslation("company");

  return (
    <Button asChild variant="link" size="xs" className="w-fit px-0">
      <Link to={MS.PAYROLL.BRANCH_TAB(branchId, "payroll-groups")}>
        <Settings2 size={12} />
        {t("payrolls.manage_payroll_groups")}
      </Link>
    </Button>
  );
};

const SubscopeRow = ({
  item,
  kind,
  paletteIndex,
  selected,
  onToggle,
}: {
  item: Subscope;
  kind: "department" | "group";
  paletteIndex: number;
  selected: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation("company");

  return (
    <label
      className={cn(
        "flex items-center justify-between gap-3 w-full rounded-xs border px-4 py-3 cursor-pointer transition",
        selected
          ? "border-primary bg-primary/10"
          : "border-border bg-white hover:border-primary/40",
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          className="shrink-0"
        />
        <Monogram kind={kind} name={item.name} paletteIndex={paletteIndex} />
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {item.name}
          </span>
          <span className="text-xs text-gray-400 truncate">
            {item.subtitle}
          </span>
        </div>
      </div>
      <span className="text-xs text-gray-500 shrink-0">
        {t("payrolls.employee_count", { count: item.employees })}
      </span>
    </label>
  );
};

const BranchExpandedContent = ({
  branch,
  selected,
  onSubscopeToggle,
}: {
  branch: IPayrollBranch;
  selected: ScopeItem[];
  onSubscopeToggle: (
    branchStringId: string,
    item: Subscope,
    kind: "department" | "group",
  ) => void;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const branchStringId = String(branch.id);
  const [deptsOpen, setDeptsOpen] = useState(true);
  const [groupsOpen, setGroupsOpen] = useState(true);
  const { data, isLoading } = useGetBranchScopeOptionsQuery({
    branchId: branch.id,
  });

  const departments: Subscope[] = (data?.data?.departments ?? []).map((d) => ({
    id: `dept-${d.id}`,
    name: d.name,
    subtitle: branch.name,
    employees: d.employeeCount,
  }));
  const groups: Subscope[] = (data?.data?.payrollGroups ?? []).map((g) => ({
    id: `grp-${g.id}`,
    name: g.name,
    subtitle: t("company:payrolls.custom_group_branch_subtitle", {
      branch: branch.name,
    }),
    employees: g.employeeCount,
  }));

  const wholeBranchSelected = selected.some(
    (s) => s.kind === "branch" && s.id === branchStringId,
  );

  const isDeptSelected = (id: string) =>
    wholeBranchSelected || selected.some((s) => s.id === id);
  const isGroupSelected = (id: string) => selected.some((s) => s.id === id);

  if (isLoading) {
    return (
      <div className="border-t border-border bg-white px-4 py-3 text-xs text-gray-400">
        {t("common:shared_ui.loading")}
      </div>
    );
  }

  if (departments.length === 0 && groups.length === 0) {
    return (
      <div className="border-t border-border bg-surface px-4 py-3 flex flex-col gap-1.5">
        <p className="text-xs text-muted-foreground">
          {t("company:payrolls.branch_has_no_departments_or_groups_hint")}
        </p>
        <ManageGroupsLink branchId={branch.id} />
      </div>
    );
  }

  return (
    <div className="border-t border-border bg-white px-4 py-3 flex flex-col gap-3">
      {departments.length > 0 && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setDeptsOpen((v) => !v)}
            className="text-xs font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1 w-fit"
          >
            {t("company:payrolls.departments")}
            <ChevronDown
              className={cn(
                "size-4 text-primary transition-transform",
                deptsOpen ? "" : "-rotate-90",
              )}
            />
          </button>
          {deptsOpen && (
            <div className="flex flex-col gap-2 max-h-[320px] overflow-auto pe-1">
              {departments.map((d, idx) => (
                <SubscopeRow
                  key={d.id}
                  item={d}
                  kind="department"
                  paletteIndex={idx}
                  selected={isDeptSelected(d.id)}
                  onToggle={() =>
                    onSubscopeToggle(branchStringId, d, "department")
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}

      {groups.length === 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-xs text-muted-foreground">
            {t("company:payrolls.no_payroll_groups_set_up_for_this_branch")}
          </p>
          <ManageGroupsLink branchId={branch.id} />
        </div>
      )}

      {groups.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setGroupsOpen((v) => !v)}
              className="text-xs font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1 w-fit"
            >
              {t("company:payrolls.custom_groups")}
              <ChevronDown
                className={cn(
                  "size-4 text-primary transition-transform",
                  groupsOpen ? "" : "-rotate-90",
                )}
              />
            </button>
            <ManageGroupsLink branchId={branch.id} />
          </div>
          {groupsOpen && (
            <div className="flex flex-col gap-2 max-h-[260px] overflow-auto pe-1">
              {groups.map((g, idx) => (
                <SubscopeRow
                  key={g.id}
                  item={g}
                  kind="group"
                  paletteIndex={idx}
                  selected={isGroupSelected(g.id)}
                  onToggle={() => onSubscopeToggle(branchStringId, g, "group")}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface IProps {
  branches: IPayrollBranch[];
  isLoading?: boolean;
  selected: ScopeItem[];
  onChange: (items: ScopeItem[]) => void;
}

export const SelectScope = ({
  branches,
  isLoading,
  selected,
  onChange,
}: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [query, setQuery] = useState("");
  const [branchesOpen, setBranchesOpen] = useState(true);
  const [expandedBranchId, setExpandedBranchId] = useState<string | null>(null);

  const branchIdInSelection =
    selected.find((s) => s.parentBranchId)?.parentBranchId ??
    selected.find((s) => s.kind === "branch")?.id ??
    null;

  const toggleBranch = (b: IPayrollBranch) => {
    const stringId = String(b.id);
    const wasWhole =
      selected.length === 1 &&
      selected[0].kind === "branch" &&
      selected[0].id === stringId;
    if (wasWhole) {
      onChange([]);
      return;
    }
    onChange([
      {
        id: stringId,
        kind: "branch",
        name: b.name,
        subtitle: "",
        employees: b.employeeCount,
      },
    ]);
    setExpandedBranchId(stringId);
  };

  const toggleSubscope = (
    branchStringId: string,
    item: Subscope,
    kind: "department" | "group",
  ) => {
    const sameBranch = branchIdInSelection === branchStringId;
    const wholeBranchSelected = selected.some(
      (s) => s.kind === "branch" && s.id === branchStringId,
    );

    let next: ScopeItem[];

    if (!sameBranch) {
      next = [
        {
          id: item.id,
          kind,
          name: item.name,
          subtitle: item.subtitle,
          employees: item.employees,
          parentBranchId: branchStringId,
        },
      ];
    } else {
      const filtered = wholeBranchSelected
        ? selected.filter((s) => s.kind !== "branch")
        : selected;

      if (filtered.some((s) => s.id === item.id)) {
        next = filtered.filter((s) => s.id !== item.id);
      } else {
        next = [
          ...filtered,
          {
            id: item.id,
            kind,
            name: item.name,
            subtitle: item.subtitle,
            employees: item.employees,
            parentBranchId: branchStringId,
          },
        ];
      }
    }

    onChange(next);
  };

  const visibleBranches = useMemo(() => {
    if (!query) return branches;
    const q = query.toLowerCase();
    return branches.filter((b) => b.name.toLowerCase().includes(q));
  }, [query, branches]);

  return (
    <div className="flex flex-col gap-4">
      {/* Search */}
      <div className="flex items-center gap-2 border border-input rounded-xs h-9 px-3 bg-white">
        <Search size={14} className="text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("company:payrolls.search_branch")}
          className="flex-1 bg-transparent text-xs placeholder:text-gray-400 focus:outline-hidden"
        />
      </div>

      {/* Empty / chips */}
      {selected.length === 0 ? (
        <div className="rounded-xs bg-gray-50 px-4 py-3 text-sm text-gray-400">
          {t("company:payrolls.nothing_selected_yet_search_or_click_to_add")}
        </div>
      ) : (
        <div className="rounded-xs bg-gray-50 px-4 py-3 flex flex-wrap gap-2">
          {selected.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-2 rounded-xs border border-primary bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary"
            >
              {s.name}
              <button
                type="button"
                onClick={() => onChange(selected.filter((x) => x.id !== s.id))}
                className="text-primary hover:text-primary/70 rounded-full p-0.5 hover:bg-white/50"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Branches */}
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setBranchesOpen((v) => !v)}
          className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-500 w-fit"
        >
          {t("company:payrolls.branches")}
          <ChevronDown
            className={cn(
              "size-4 text-primary transition-transform",
              branchesOpen ? "" : "-rotate-90",
            )}
          />
        </button>

        {branchesOpen && (
          <div className="flex flex-col gap-2">
            {isLoading && (
              <div className="text-sm text-gray-400 py-4 text-center">
                {t("company:payrolls.loading_branches")}
              </div>
            )}
            {!isLoading && visibleBranches.length === 0 && (
              <div className="text-sm text-gray-400 py-4 text-center">
                {t("company:payrolls.no_branches_found")}
              </div>
            )}
            {visibleBranches.map((b) => {
              const stringId = String(b.id);
              const isExpanded = expandedBranchId === stringId;
              const wholeSelected = selected.some(
                (s) => s.kind === "branch" && s.id === stringId,
              );
              const isHighlighted = wholeSelected || isExpanded;

              return (
                <div
                  key={b.id}
                  className={cn(
                    "rounded-xs border overflow-hidden transition",
                    isHighlighted
                      ? "border-primary bg-primary/10/40"
                      : "border-border bg-white",
                  )}
                >
                  {/* Branch row */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Checkbox
                        checked={wholeSelected}
                        onCheckedChange={() => toggleBranch(b)}
                        className="shrink-0"
                      />
                      <Monogram kind="branch" name={b.name} />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {b.name}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-gray-500">
                        {t("company:payrolls.employee_count", {
                          count: b.employeeCount,
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedBranchId((cur) =>
                            cur === stringId ? null : stringId,
                          )
                        }
                        className="text-primary p-1"
                      >
                        {isExpanded ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded content — loads scope options on demand */}
                  {isExpanded && (
                    <BranchExpandedContent
                      branch={b}
                      selected={selected}
                      onSubscopeToggle={toggleSubscope}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
